import { TypeNormalizer, CARD_TYPES } from './types';
import { normalizePersonCard } from './adapters/PersonCardAdapter';
import { normalizeEventCard } from './adapters/EventCardAdapter';
import {
  normalizeRegisterCard,
  normalizeRegisterEventCard,
} from './adapters/RegistrationCardAdapter';
import { normalizeNewsCard } from './adapters/NewsCardAdapter';
import {
  normalizeBaseCard,
  normalizeTeaserSchoolCard,
  normalizeCourseCard,
  normalizeTrainingCard,
  normalizeProjectCard,
  normalizeProductCard,
  normalizePanelCard,
} from './adapters/ContentCardAdapters';

/**
 * Strategy pattern implementation for card transformation
 * Maps card types to their respective adapter functions
 */
export class CardAdapterStrategy {
  private static readonly adapters: Record<string, TypeNormalizer> = {
    // Person types
    person: normalizePersonCard,

    // Event types
    event: normalizeEventCard,

    // News types
    news: normalizeNewsCard,

    // Registration types
    register: normalizeRegisterCard,
    'register-event': normalizeRegisterEventCard,

    // Course types
    course: normalizeCourseCard,

    // Training types
    training: normalizeTrainingCard,

    // Project types
    project: normalizeProjectCard,

    // Research types
    researchfield: normalizeBaseCard,
    researchfocus: normalizeBaseCard,

    // Topic types
    topic: normalizeBaseCard,

    // School types (used for teaser cards)
    'teaser-school': normalizeTeaserSchoolCard,

    // Specialization types
    specialization: normalizeBaseCard,

    // Service types
    service: normalizeBaseCard,

    // Product types
    product: normalizeProductCard,

    // Panel types
    panel: normalizePanelCard,
    'panel-alternative': normalizePanelCard,
  };

  /**
   * Gets the appropriate adapter for a given card type
   */
  static getAdapter(cardType: string): TypeNormalizer | null {
    return this.adapters[cardType] || null;
  }

  /**
   * Determines the card type from content type
   */
  static getCardType(contentType: string): string {
    return CARD_TYPES[contentType] || 'product';
  }

  /**
   * Checks if an adapter exists for the given card type
   */
  static hasAdapter(cardType: string): boolean {
    return cardType in this.adapters;
  }

  /**
   * Gets all available card types
   */
  static getAvailableTypes(): string[] {
    return Object.keys(this.adapters);
  }

  /**
   * Registers a new adapter for a card type
   * Useful for extending functionality
   */
  static registerAdapter(cardType: string, adapter: TypeNormalizer): void {
    this.adapters[cardType] = adapter;
  }

  /**
   * Removes an adapter for a card type
   */
  static unregisterAdapter(cardType: string): void {
    delete this.adapters[cardType];
  }
}
